import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { AuthShell } from '@/components/layout/AuthShell';
import { authClient } from '@/lib/api/authClient';
import { toast } from '@/lib/notify';

import { MarketingCarousel } from '../components/MarketingCarousel';
import { PasswordInput } from '../components/PasswordInput';
import { authResultSchema } from '../schemas/authSchemas';
import { useAuthStore } from '../store/authStore';

const acceptInvitationSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters').max(256),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;

export function AcceptInvitationScreen() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInvitationInput>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  async function onSubmit({ password }: AcceptInvitationInput) {
    try {
      const raw = await authClient
        .post('auth/accept-invitation', { json: { token, password } })
        .json();
      const result = authResultSchema.parse(raw);
      setSession(result);
      // Invited members skip onboarding — go straight to the dashboard.
      navigate('/dashboard', { replace: true });
    } catch {
      toast.error('Invalid or expired invitation link. Please request a new invitation.');
    }
  }

  if (!token) {
    return (
      <AuthShell marketing={<MarketingCarousel />}>
        <div className="w-full max-w-[400px] text-center space-y-4">
          <h1 className="text-2xl font-bold text-black-logo">Invalid Link</h1>
          <p className="text-sm text-gray66">
            This invitation link is missing required information. Please use the link from your
            invitation email.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell marketing={<MarketingCarousel />}>
      <div className="w-full max-w-[400px] space-y-6 lg:space-y-8">
        <div className="space-y-2">
          <h1 className="text-2xl lg:text-[28px] font-bold leading-tight text-black-logo">
            Set Your Password
          </h1>
          <p className="text-sm text-gray66 leading-[150%]">
            You've been invited to join. Create a password to activate your account.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="accept-password" className="sc-input-label" data-required>
              Password
            </label>
            <PasswordInput
              id="accept-password"
              placeholder="Create a password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="accept-confirm-password" className="sc-input-label" data-required>
              Confirm Password
            </label>
            <PasswordInput
              id="accept-confirm-password"
              placeholder="Confirm your password"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full mt-2">
            {isSubmitting ? 'Joining…' : 'Join Now'}
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
